import { useSelector } from "react-redux";
import { findCorePlatformEntitlement, isCoreTopTier } from "./planEntitlements";

/** Legacy negotiated platform contracts — pricing is sales-led, not self-serve. */
const SALES_LED_PACKAGE_IDS = new Set([100, 110, 120, 200, 300]);

function activeEntitlements(subscriptionRow) {
  return (subscriptionRow?.entitlements || []).filter(
    (entry) => entry?.status === "active" || entry?.status === "trial",
  );
}

/**
 * Enterprise / sales-led customer — never self-serve checkout or list prices.
 *
 * Prefer API CustomerType on EnterpriseCustomers (customer/account table).
 * Fallback: legacy PackageId + entitlement tier_key=enterprise heuristics.
 */
export function isEnterpriseCustomer(subscriptionRow) {
  if (!subscriptionRow) return false;

  const explicit = String(subscriptionRow.CustomerType || "").trim().toLowerCase();
  if (explicit === "enterprise") return true;
  if (explicit === "smb") return false;

  const packageId = Number(subscriptionRow.PackageId);
  if (SALES_LED_PACKAGE_IDS.has(packageId)) return true;

  for (const entry of activeEntitlements(subscriptionRow)) {
    if (String(entry.tier_key || "").toLowerCase() === "enterprise") {
      return true;
    }
  }

  const core = findCorePlatformEntitlement(subscriptionRow.entitlements || []);
  return isCoreTopTier(core);
}

/** SMB / public self-serve — may see prices and Stripe flows where product allows. */
export function isSelfServeCustomer(subscriptionRow) {
  return Boolean(subscriptionRow) && !isEnterpriseCustomer(subscriptionRow);
}

/** @deprecated Prefer isEnterpriseCustomer — kept for existing call sites. */
export function shouldHidePlatformPricing(subscriptionRow) {
  return isEnterpriseCustomer(subscriptionRow);
}

export function useIsEnterpriseCustomer() {
  const subscriptionRow = useSelector((state) => state.userReducer);
  return isEnterpriseCustomer(subscriptionRow);
}

export function useIsSelfServeCustomer() {
  const subscriptionRow = useSelector((state) => state.userReducer);
  return isSelfServeCustomer(subscriptionRow);
}

/** @deprecated Prefer useIsEnterpriseCustomer */
export function useHidePlatformPricing() {
  return useIsEnterpriseCustomer();
}
